import { useInfiniteQuery } from '@tanstack/react-query';

import { getUsers } from '@/generated/client/users/users';
import type { GetUsersParams } from '@/generated/model';

import { userKeys } from './userQueryKeys';

export function useInfiniteUserList(params: Omit<GetUsersParams, 'offset' | 'limit'>, pageSize: number) {
  async function fetchUsers(page: number) {
    const response = await getUsers({
      ...params,
      offset: page * pageSize,
      limit: pageSize,
    });
    return response.data;
  }

  const usersQuery = useInfiniteQuery({
    queryKey: userKeys.infiniteList(params, pageSize),
    queryFn: ({ pageParam }) => fetchUsers(pageParam),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage.length < pageSize) {
        return undefined;
      }

      return allPages.length;
    },
  });
  const users = usersQuery.data?.pages.flat() ?? [];

  return {
    users,
    hasMore: usersQuery.hasNextPage,
    isLoading: usersQuery.isLoading,
    isFetchingNextPage: usersQuery.isFetchingNextPage,
    isError: usersQuery.isError,
    loadMoreUsers: usersQuery.fetchNextPage,
    retryUsers: usersQuery.refetch,
  };
}
